import { Injectable } from '@angular/core';
import { TableFilterDialogResult } from "@valg-shared/components/table/table/valg-filter-dialog/table-filter-dialog.data";
import { UserInfoDto } from '@valg-shared/services/auth-service/user-info-dto';

@Injectable({
  providedIn: 'root'
})
export class FilterStateService {
  private readonly filterPrefix = 'valg-filter-';
  private readonly districtFilterPrefix = 'district-';

  isValidKey(key: string | undefined | null): boolean {
    return key !== undefined && key !== null && key !== '';
  }

  generateFilterKey(userInfo: UserInfoDto, electionId: string, filterPostfix: string): string | undefined {
    if (!userInfo?.userId || !electionId || !filterPostfix) {
      return undefined;
    }
    return `${this.filterPrefix}${userInfo.userId}-${electionId}-${filterPostfix}`;
  }
  
  generateDistrictFilterKey(userInfo: UserInfoDto, electionId: string, districtId: string, filterPostfix: string): string | undefined {
    if (!userInfo?.userId || !electionId || !districtId || !filterPostfix) {
      return undefined;
    }
    return `${this.filterPrefix}${userInfo.userId}-${electionId}-${this.districtFilterPrefix}${districtId}-${filterPostfix}`;
  }
  
  hasFilter(key: string | undefined | null): boolean {
    if (!this.isValidKey(key)) return false;
    const filterJson = localStorage.getItem(key!);
    return !!filterJson;
  }

  isFilterEmpty(filter: TableFilterDialogResult | undefined | null): boolean {
    if (!filter) return true;
    
    if (!filter.columns || filter.columns.length === 0) return true;
    
    return filter.columns.every(column => 
      !column.value || column.value.length === 0
    );
  }
  
  saveFilter(key: string | undefined | null, filter: TableFilterDialogResult): void {
    if (!this.isValidKey(key)) return;
    
    if (this.isFilterEmpty(filter)) {
      this.clearFilter(key);
      return;
    }
    
    const filterJson = JSON.stringify(filter);
    localStorage.setItem(key!, filterJson);
  }
  
  saveDistrictFilter(userInfo: UserInfoDto, electionId: string, districtId: string, filterPostfix: string, filter: TableFilterDialogResult): void {
    const key = this.generateDistrictFilterKey(userInfo, electionId, districtId, filterPostfix);
    this.saveFilter(key, filter);
  }
  
  loadFilter<T extends TableFilterDialogResult>(key: string | undefined | null): T | undefined {
    if (!this.isValidKey(key)) return undefined;
    
    const filterJson = localStorage.getItem(key!);
    if (!filterJson) {
      return undefined;
    }
    
    const filter = JSON.parse(filterJson) as T;
    
    if (this.isFilterEmpty(filter)) {
      return undefined;
    }
    
    return filter;
  }
  
  loadDistrictFilter<T extends TableFilterDialogResult>(userInfo: UserInfoDto, electionId: string, districtId: string, filterPostfix: string): T | undefined {
    const key = this.generateDistrictFilterKey(userInfo, electionId, districtId, filterPostfix);
    return this.loadFilter<T>(key);
  }
  
  clearFilter(key: string | undefined | null): void {
    if (!this.isValidKey(key)) return;
    
    localStorage.removeItem(key!);
  }
  
  clearDistrictFilter(userInfo: UserInfoDto, electionId: string, districtId: string, filterPostfix: string): void {
    const key = this.generateDistrictFilterKey(userInfo, electionId, districtId, filterPostfix);
    this.clearFilter(key);
  }
}
